/**
 * How the Goals workspace reaches the screen (F-JR-37, F-JR-40).
 *
 * The workspace is a full-viewport route rather than a component the Journal
 * root nests, and it is *presented* rather than navigated to: F-NAV-23 forbids
 * reading state back out of the URL, so a refresh on `/journal/goals` must not
 * resurrect a surface that reads a Journal which may be sitting behind Journal
 * Lock. The host that opens it calls `present()`, and the shell mounts the
 * route because the same call sets the full-viewport path.
 *
 * The **goals themselves are not in the request.** They come from the shared
 * privacy session's library, the same value every other Journal surface reads,
 * because a workspace handed its own copy would go stale the moment a lifecycle
 * change rewrote a goal, and a workspace that loaded its own would be a surface
 * that could be made to read while concealed.
 *
 * The repository *is* in the request, for the reason the reader takes it that
 * way too: the storage ports are not part of the app's `PortBundle`, and the
 * host that opened this surface already holds one. Constructing a second here
 * would be a second connection disagreeing with the first.
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { JournalGoalId } from "@ff/domain";
import type { JournalRepositoryPort } from "@ff/domain/ports";

import { useNavigation } from "../../../app-state/navigation";
import { fullViewportEntry } from "../../../shell/routeTable";
import type { BeginGoalReflection } from "./handoff";

/** The route's path in `shell/routeTable.ts`. Presented, never navigated to. */
export const JOURNAL_GOALS_ROUTE_PATH = "/journal/goals";

/**
 * Resolved at module scope and **by name**, the way the shell resolves Ringside
 * and Settings resolves the drill library. `fullViewportEntry` throws for a path
 * the table no longer declares, so a renamed surface fails at import with the
 * path it wanted — instead of `presentFullViewport` setting a path the shell has
 * no component for and the surface silently never appearing.
 */
const PRESENTED_PATH = fullViewportEntry(JOURNAL_GOALS_ROUTE_PATH).path;

export interface JournalGoalsPresentation {
  /** Absent means this build cannot write goals; every write action goes inert. */
  readonly repository?: JournalRepositoryPort;
  /**
   * Allocates the id of a goal that does not exist yet.
   *
   * Injected rather than taken from `crypto`, because a feature that reached
   * for a browser global would be unable to prove its own persistence rules.
   * The composition root fills it from `IdPort.journalGoalId`
   * (`app-state/journalHost.tsx`); a test supplies whatever id it wants to
   * assert. Absent makes the create action inert (§8.6).
   */
  readonly newGoalId?: () => JournalGoalId;
  /** F-JR-40: opens the composer on kind `goalReflection` with the goal link. */
  readonly beginGoalReflection?: BeginGoalReflection;
  /** Runs after a durable goal write, so the host can re-read the library. */
  readonly onChanged?: () => void;
  /** Runs once the workspace has been dismissed. */
  readonly onDismiss?: () => void;
  /** Pinned only by a test; the app formats in the reader's own locale. */
  readonly locale?: string;
}

export interface JournalGoalsPresentationState {
  readonly presentation: JournalGoalsPresentation | null;
  readonly present: (presentation: JournalGoalsPresentation) => void;
  readonly dismiss: () => void;
}

const JournalGoalsPresentationContext =
  createContext<JournalGoalsPresentationState | null>(null);

export function JournalGoalsPresentationProvider({
  children,
}: {
  readonly children?: ReactNode;
}): ReactNode {
  const navigation = useNavigation();
  const [presentation, setPresentation] = useState<JournalGoalsPresentation | null>(null);

  const { presentFullViewport, dismissFullViewport } = navigation;

  const present = useCallback(
    (next: JournalGoalsPresentation) => {
      // Stored before the path is set: the shell mounts the route synchronously
      // from the path, and a route that mounted first would render its empty
      // branch for one commit.
      setPresentation(next);
      presentFullViewport(PRESENTED_PATH);
    },
    [presentFullViewport],
  );

  const dismiss = useCallback(() => {
    dismissFullViewport();
    setPresentation(null);
  }, [dismissFullViewport]);

  const value = useMemo<JournalGoalsPresentationState>(
    () => ({ presentation, present, dismiss }),
    [dismiss, present, presentation],
  );

  return (
    <JournalGoalsPresentationContext.Provider value={value}>
      {children}
    </JournalGoalsPresentationContext.Provider>
  );
}

/**
 * Null rather than a throw. A Journal workspace that renders the goals list in
 * its own column legitimately has no presentation context above it, and a host
 * that cannot present is a host whose "Goals workspace" action is simply absent
 * — the §8.6 shape — rather than one that crashes on mount.
 */
export function useOptionalJournalGoalsPresentation(): JournalGoalsPresentationState | null {
  return useContext(JournalGoalsPresentationContext);
}
